import { ArrowUpFromLine, CheckCheck, Reply, Send, Star, Trash2, X, File } from 'lucide-react';
import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import VideoPlayer from '../components/VideoPlayer';
import AudioPlayer from '../components/AudioPlayer';
import LinkHighlighter from '../components/LinkHighlighter';
import BeautifiedCodeViewer from '../components/BeautifiedCodeViewer';
import MapCard from '../components/MapCard';

function BotChat(props) {
    const {
        chatMessages,
        highlightReply,
        senderContainerColor,
        receiverContainerColor,
        senderTextColor,
        receiverTextColor,
        messagesContainerRef,
        scrollToMessage,
        authUserId,
        checkFileType,
        openInNewTab,
        formatFile,
        handleReplyMessage,
        isProbablyCode,
        handleDeleteMessage,
        senderDateTextColor,
        receiverDateTextColor,
        color,
        senderLinkTextColor,
        receiverLinkTextColor,
        replyMessageData,
        sendButtonColor,
        setReplyMessageData,
        fetchBotChatHistory,
        activeBotChat,
        chatPageLimit,
        hasMoreMessagesRef,
        ctaButtonColor, ctaButtonText
    } = props;

    const divRef = useRef();
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [prevScrollHeight, setPrevScrollHeight] = useState(null);
    const [isLoadings, setIsLoading] = useState(false);
    const isFetchingRef = useRef(false);
    const scrollTimeoutRef = useRef(null);

    // Memoize flattened and sorted chats
    const allChats = useMemo(() => {
        if (!Array.isArray(chatMessages)) return [];
        return chatMessages
            .flatMap(day => day)
            .sort((a, b) => new Date(b.date) - new Date(a.date));
    }, [chatMessages]);

    // Memoize visible chats
    const visibleChats = useMemo(() => {
        return allChats.slice(0, page * pageSize);
    }, [allChats, page]);


    const handleScroll = useCallback(() => {
        const el = divRef.current;

        // Prevent execution if element doesn't exist or already loading
        if (!el || isLoadings || isFetchingRef.current || !hasMoreMessagesRef.current) return;

        // Clear existing timeout
        if (scrollTimeoutRef.current !== null) {
            clearTimeout(scrollTimeoutRef.current);
            scrollTimeoutRef.current = null;
        }

        // Debounce scroll handler
        scrollTimeoutRef.current = setTimeout(() => {
            const scrollTop = Math.abs(el.scrollTop); // Handle negative scrollTop
            const scrollHeight = el.scrollHeight;
            const clientHeight = el.clientHeight;

            // Calculate distance from bottom (with 50px threshold)
            const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

            console.log('Scroll Debug:', {
                scrollTop,
                scrollHeight,
                clientHeight,
                distanceFromBottom,
                isAtBottom: distanceFromBottom <= 50
            });

            // ✅ Check if scrolled near bottom
            if (distanceFromBottom <= 50 && !isFetchingRef.current && hasMoreMessagesRef.current) {
                console.log('📥 Fetching more messages...');

                const currentScrollHeight = el.scrollHeight;

                // Set loading states
                setIsLoading(true);
                isFetchingRef.current = true;
                setPrevScrollHeight(currentScrollHeight);

                // Fetch chat history
                fetchBotChatHistory(activeBotChat, chatPageLimit)
                    .catch(error => {
                        console.error('Error fetching chat history:', error);
                    })
                    .finally(() => {
                        // Reset loading state after a short delay
                        setTimeout(() => {
                            setIsLoading(false);
                            isFetchingRef.current = false;
                        }, 300);
                    });
            }

            scrollTimeoutRef.current = null;
        }, 150); // 150ms debounce
    }, [isLoadings, fetchBotChatHistory, activeBotChat, chatPageLimit, hasMoreMessagesRef]);
    console.log('activeBotChat', activeBotChat)
    // ✅ Maintain scroll position after loading new messages
    useEffect(() => {
        if (prevScrollHeight === null) return;

        const el = divRef.current;
        if (!el) return;

        requestAnimationFrame(() => {
            const newScrollHeight = el.scrollHeight;
            const heightDifference = newScrollHeight - prevScrollHeight;

            // Adjust scroll position to maintain user's view
            el.scrollTop -= heightDifference;

            console.log('📍 Scroll adjusted:', {
                prevHeight: prevScrollHeight,
                newHeight: newScrollHeight,
                difference: heightDifference
            });

            setPrevScrollHeight(null);
        });
    }, [chatMessages, prevScrollHeight]);

    // ✅ Reset loading state when new messages arrive
    useEffect(() => {
        if (isFetchingRef.current && !isLoadings) {
            isFetchingRef.current = false;
        }
    }, [chatMessages, isLoadings]);

    // ✅ Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current !== null) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
        };
    }, []);

    // Render reply preview
    const renderReplyPreview = useCallback((message) => {
        if (!message?.replyMessageId) return null;

        return (
            <div
                className={`p-2 rounded border-l-4 cursor-pointer shadow mb-2
                    ${message.isSent ? 'bg-gray-600 border-teal-600' : 'bg-gray-100 border-teal-600'}`}
                onClick={(e) => {
                    e.stopPropagation();
                    scrollToMessage(message.replyMessageId._id);
                }}
            >
                <div className={`text-xs font-medium mb-1 text-black ${message.sender === 'me' ? 'text-primary' : 'text-primary'
                    }`}>
                    {message?.replyMessageId?.fromUserId?._id === authUserId
                        ? "You"
                        : message?.replyMessageId?.fromUserId?.name || "Unknown User"
                    }
                </div>
                <div className="text-xs text-gray-700 truncate">
                    {message?.replyMessageId?.messageContent || "Message content unavailable"}
                </div>
            </div>
        );
    }, [authUserId, scrollToMessage]);

    // Render file preview
    const renderFilePreview = useCallback((message) => {
        const fileType = checkFileType(message.fileType);

        switch (fileType) {
            case 'image':
                return (
                    <div className="flex justify-center items-center mb-1">
                        <img
                            src={message.link}
                            alt="Preview"
                            className="max-h-32 rounded cursor-pointer"
                            onClick={() => openInNewTab(message.link)}
                            loading="lazy"
                        />
                    </div>
                );
            case 'video':
                return <VideoPlayer src={message.link} color={color} />;
            case 'audio':
                return <AudioPlayer src={message.link} color={color} />;
            case 'application':
                return (
                    <div
                        className="flex items-center bg-gray-200 p-2 rounded cursor-pointer mb-1"
                        onClick={() => openInNewTab(message.link)}
                    >
                        <File size={20} className="text-black mr-2" />
                        <p className="text-sm font-medium text-black">
                            Document{formatFile(message.link)}
                        </p>
                    </div>
                );
            default:
                return null;
        }
    }, [checkFileType, openInNewTab, formatFile, color]);

    // Render message actions
    const renderMessageActions = useCallback((message) => {
        if (message.isDeleted) return null;

        return (
            <div
                className={`absolute top-1/2 -translate-y-1/2 z-10 flex items-center gap-2 px-2
                    ${message.sender === 'me' ? 'right-full ml-2' : 'left-full mr-2'}
                    opacity-0 group-hover:opacity-100 transition-opacity`}
            >
                <Reply
                    onClick={() => handleReplyMessage(message)}
                    className="text-gray-400 hover:text-primary cursor-pointer"
                    size={18}
                />
                {message.sender === 'me' && (
                    <Trash2
                        onClick={() => handleDeleteMessage(message)}
                        className="text-gray-400 hover:text-primary cursor-pointer"
                        size={18}
                    />
                )}
            </div>
        );
    }, [handleReplyMessage, handleDeleteMessage]);

    // Render message content
    const renderMessageContent = useCallback((message) => {
        const isMe = message.sender === 'me';

        if (message.isDeleted) {
            return (
                <div
                    className={`p-2 text-xs italic rounded-xl shadow-md whitespace-pre-wrap break-words  
                        ${isMe ? 'rounded-br-none' : 'rounded-bl-none'}`}
                    style={{
                        backgroundColor: isMe ? senderContainerColor : receiverContainerColor,
                        color: isMe ? senderTextColor : receiverTextColor,
                    }}
                >
                    {isMe ? 'You deleted this message' : "This message was deleted"}
                </div>
            );
        }

        return (
            <div
                className={`p-2 rounded-xl shadow-md whitespace-pre-wrap break-words  
                    ${isMe ? 'rounded-br-none' : 'rounded-bl-none'}`}
                style={{
                    backgroundColor: isMe ? senderContainerColor : receiverContainerColor,
                    color: isMe ? senderTextColor : receiverTextColor,
                }}
            >
                {renderReplyPreview(message)}
                {renderFilePreview(message)}

                {message.type === "L" && (
                    <MapCard data={message.text} />
                )}

                {/* TEXT / LINK / CODE */}
                {!isProbablyCode(message.text) &&
                    checkFileType(message.fileType) !== 'image' &&
                    message.text !== '' &&
                    message.type !== 'L' && (
                        <div className="text-sm mt-1">
                            <LinkHighlighter
                                text={message?.text || ""}
                                color={isMe ? senderLinkTextColor : receiverLinkTextColor}
                            />
                        </div>
                    )}

                {isProbablyCode(message.text) && (
                    <BeautifiedCodeViewer code={message.text} />
                )}
            </div>
        );
    }, [senderContainerColor, receiverContainerColor, senderTextColor, receiverTextColor,
        renderReplyPreview, renderFilePreview, isProbablyCode, checkFileType,
        senderLinkTextColor, receiverLinkTextColor]);

    return (
        <Fragment>
            <div
                ref={divRef}
                onScroll={handleScroll}
                tabIndex={-1}
                className="flex-1 p-3 overflow-y-auto flex flex-col-reverse"
            >
                {/* ✅ Loading indicator */}
                {isLoadings && (
                    <div className="text-center py-3 text-gray-500 text-sm animate-pulse">
                        Loading more messages...
                    </div>
                )}

                {[...chatMessages].map((chat, chatIndex) => (
                    <div key={chatIndex + 1}>
                        <div className="text-xs bg-primary/20 text-primary px-2 py-1 mx-auto my-4 backdrop-blur-sm border-b w-full flex items-center justify-center border-gray-500">
                            {chat?.date}
                        </div>

                        {[...chat.chats].reverse().map((message, index) => {
                            const isMe = message.sender === 'me';
                            const isReceiver = message.sender === 'reciver';

                            return (
                                <div
                                    key={index + 1}
                                    className={`mb-2 flex ${isMe ? 'justify-end' : 'justify-start'}`}
                                >


                                    <div className="flex flex-col max-w-[80%]">
                                        {isReceiver && (
                                            <span
                                                className="text-xs font-semibold mb-1"
                                            // style={{ color: getColorByName(message?.name) }}
                                            >
                                                {message?.name}
                                            </span>
                                        )}

                                        <div
                                            className={`p-2 rounded-lg shadow-md break-words whitespace-pre-wrap ${isMe
                                                ? 'bg-blue-500 text-white rounded-br-none'
                                                : 'bg-gray-200 text-gray-800 rounded-bl-none'
                                                }`}
                                            style={{
                                                backgroundColor: isMe ? senderContainerColor : receiverContainerColor,
                                                color: isMe ? senderTextColor : receiverTextColor
                                            }}
                                        >
                                            {/* Render file type previews */}
                                            {checkFileType(message.fileType) === 'image' && (
                                                <div className="flex justify-center items-center mb-1">
                                                    <img
                                                        onClick={() => openInNewTab(message.link)}
                                                        src={message.link}
                                                        alt="Preview"
                                                        className="max-h-28 rounded cursor-pointer"
                                                    />
                                                </div>
                                            )}

                                            {checkFileType(message.fileType) === 'video' && (
                                                <VideoPlayer src={message.link} color={color} />
                                            )}

                                            {checkFileType(message.fileType) === 'audio' && (
                                                <AudioPlayer src={message.link} color={color} />
                                            )}

                                            {checkFileType(message.fileType) === 'application' && (
                                                <div
                                                    className="flex items-center bg-gray-100 p-2 rounded cursor-pointer mb-1"
                                                    onClick={() => openInNewTab(message.link)}
                                                >
                                                    <File size={20} className="text-black mr-2" />
                                                    <p className="text-sm text-black">Document{formatFile(message.link)}</p>
                                                </div>
                                            )}

                                            {/* Render text/code */}
                                            <div className="text-sm">
                                                {/* {!isProbablyCode(message.text) && checkFileType(message.fileType) != "image" && message.text !== '' && message.type !== 'L' && (
                                                                        <LinkHighlighter
                                                                            text={message.text}
                                                                            color={isMe ? senderLinkTextColor : receiverLinkTextColor}
                                                                        />
                                                                    )} */}
                                                {<LinkHighlighter text={message.text} color={message.sender === 'me' ? "#fff" : "Blue"} />}

                                                {(isProbablyCode(message?.text) && message?.text) && <BeautifiedCodeViewer code={message?.text} />}
                                            </div>
                                            {message?.type === "cta" &&
                                                message?.ctaData?.map((btn, i) => (
                                                    <div
                                                        key={i + 1}

                                                        className="px-3 py-1 text-xs w-full justify-center mt-2  text-white rounded-md hover:bg-primary flex items-center disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                                                        style={{ backgroundColor: ctaButtonColor || "#000", color: ctaButtonText || "#fff" }}
                                                        onClick={() => window.open(btn?.url, '_blank', 'noopener,noreferrer')}
                                                    >

                                                        {btn.label}
                                                    </div>
                                                ))
                                            }
                                        </div>

                                        <span
                                            className="text-xs mt-1 self-start"
                                            style={{
                                                color: isMe ? senderDateTextColor : receiverDateTextColor
                                            }}
                                        >
                                            {message.time}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>


            {/* Reply Preview */}
            {replyMessageData && (
                <div className="px-4 py-2 bg-gray-100 border-b border-gray-200 flex items-start sm:items-center">
                    <div
                        className="w-1 h-12 rounded-full mr-3 flex-shrink-0"
                        style={{ backgroundColor: sendButtonColor || "#000" }}
                    />

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                                Replying to{' '}
                                {replyMessageData.sender === "reciver"
                                    ? replyMessageData?.user?.name
                                    : "You"
                                }
                            </span>
                            <button
                                onClick={() => setReplyMessageData('')}
                                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <p className="text-sm text-gray-800 mt-1 w-full line-clamp-2">
                            {replyMessageData?.text || "No message content"}
                        </p>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default memo(BotChat);